import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CloseCircleOutlined } from '@ant-design/icons';
import parse from 'html-react-parser';
import SwiperCore, { Controller } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import benefits from './benefits.json';
import './style.scss';

SwiperCore.use([Controller]);

const asset = filename => {
  const image = require(`./assets/${filename}`);
  return image.default || image;
};
const previousBenefitsIcon = asset('prevlevelBenefits@2x.9460461.png');

function BenefitValues({ values, qualify = false }) {
  return (
    <div className={`benefit-values ${qualify ? 'qualification' : 'earning'}`}>
      <div className="value-entry">
        <div>{values.left.value}</div>
        <div>{values.left.key}</div>
      </div>
      <span>{qualify ? '或' : '='}</span>
      <div className="value-entry">
        <div>{values.right.value}</div>
        <div>{values.right.key}</div>
      </div>
    </div>
  );
}

function BenefitDialog({ benefit, onClose }) {
  const closeRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    const trigger = document.activeElement;
    closeRef.current.focus();
    return () => {
      if (trigger && trigger.isConnected) trigger.focus();
    };
  }, []);

  const handleKeyDown = event => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    } else if (event.key === 'Tab') {
      event.preventDefault();
      const target = document.activeElement === closeRef.current ? scrollRef : closeRef;
      target.current.focus();
    }
  };

  return (
    <div className="benefit-modal-overlay" onClick={onClose}>
      <section
        className="benefit-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="shangrila-benefit-title"
        onClick={event => event.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <button ref={closeRef} className="modal-close" type="button" aria-label="关闭权益详情" title="关闭权益详情" onClick={onClose}>
          <CloseCircleOutlined />
        </button>
        <div ref={scrollRef} className="modal-scroll" tabIndex={0} role="document" aria-label={`${benefit.benefitTitle}说明`}>
          <h2 id="shangrila-benefit-title" className="modal-title">{benefit.benefitTitle}</h2>
          <div className="modal-content">{parse(benefit.description || '')}</div>
          {benefit.footnotes && <div className="modal-footnotes">{parse(benefit.footnotes)}</div>}
        </div>
      </section>
    </div>
  );
}

export default function ShangriLaBenefits() {
  const pageRef = useRef(null);
  const [cardSwiper, setCardSwiper] = useState(null);
  const [contentSwiper, setContentSwiper] = useState(null);
  const [activeTier, setActiveTier] = useState(0);
  const [selectedBenefit, setSelectedBenefit] = useState(null);
  const closeBenefit = useCallback(() => setSelectedBenefit(null), []);

  useLayoutEffect(() => {
    const host = pageRef.current.closest('.container');
    if (!host) return;
    host.classList.add('shangrila-benefits-host');
    return () => host.classList.remove('shangrila-benefits-host');
  }, []);

  useEffect(() => {
    if (!cardSwiper || !contentSwiper || cardSwiper.destroyed || contentSwiper.destroyed) return;
    cardSwiper.controller.control = contentSwiper;
    contentSwiper.controller.control = cardSwiper;
    return () => {
      if (!cardSwiper.destroyed) cardSwiper.controller.control = undefined;
      if (!contentSwiper.destroyed) contentSwiper.controller.control = undefined;
    };
  }, [cardSwiper, contentSwiper]);

  const selectTier = index => {
    if (cardSwiper && !cardSwiper.destroyed) cardSwiper.slideTo(index);
    else if (contentSwiper && !contentSwiper.destroyed) contentSwiper.slideTo(index);
    setActiveTier(index);
  };

  const handleTierKeyDown = event => {
    const offset = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!offset) return;
    event.preventDefault();
    selectTier(Math.max(0, Math.min(benefits.length - 1, activeTier + offset)));
  };

  return (
    <main ref={pageRef} className="shangrila-benefits">
      <div className="benefits-page" aria-hidden={selectedBenefit ? true : undefined}>
        
        <h1 className="hotel-member-title">
          <img className="hotel-member-logo shangrila-logo" src="https://staticfile.badazhou.com/20260921/7919fb1c16f4b52aec55db97e603bdcc.webp" alt="" />
          <span>香格里拉会-会员礼遇</span>
        </h1>
        <div className="cards-region" onKeyDown={handleTierKeyDown}>
          <Swiper
            className="card-container"
            slidesPerView={1.5}
            centeredSlides
            onSwiper={setCardSwiper}
            onSlideChange={swiper => setActiveTier(swiper.activeIndex)}
          >
            {benefits.map((tier, index) => (
              <SwiperSlide key={tier.gcLevel}>
                <button type="button" className="member-card" aria-label={`${tier.name}会籍`} aria-pressed={activeTier === index} onClick={() => selectTier(index)}>
                  <img src={asset(tier.card)} alt={`${tier.name}会员卡`} draggable="false" />
                </button>
              </SwiperSlide>
            ))}
          </Swiper>
          <div className="card-pagination" aria-label="选择会籍">
            {benefits.map((tier, index) => (
              <button
                key={tier.gcLevel}
                type="button"
                className={activeTier === index ? 'active' : ''}
                aria-label={`查看${tier.name}权益`}
                aria-pressed={activeTier === index}
                onClick={() => selectTier(index)}
              ><span /></button>
            ))}
          </div>
        </div>
        <Swiper className="content-container" centeredSlides onSwiper={setContentSwiper} onSlideChange={swiper => setActiveTier(swiper.activeIndex)}>
          {benefits.map((tier, index) => (
            <SwiperSlide key={tier.gcLevel} aria-hidden={activeTier !== index}>
              <div className="triangle" />
              <section className="tier-content" aria-label={`${tier.name}会员权益`}>
                <h2 className="tier-title"><span />{tier.name}<span /></h2>
                <div className="tier-scroll" tabIndex={activeTier === index ? 0 : -1}>
                  {tier.tierQualify ? (
                    <>
                      <h3 className="benefit-label">会籍资格</h3>
                      <BenefitValues values={tier.tierQualify} qualify />
                    </>
                  ) : <p className="level-description">{tier.levelText}</p>}
                  <BenefitValues values={tier.pointQualify} />
                  <h3 className="benefit-label">会员权益</h3>
                  <div className="benefits-grid">
                    {index > 0 && (
                      <button className="benefit-item" type="button" tabIndex={activeTier === index ? 0 : -1} onClick={() => selectTier(index - 1)}>
                        <img src={previousBenefitsIcon} alt="" />
                        <span>享受所有{benefits[index - 1].name}级会员权益</span>
                      </button>
                    )}
                    {tier.benefitGroups.map(benefit => (
                      <button
                        className="benefit-item"
                        key={benefit.benefitTitle}
                        type="button"
                        tabIndex={activeTier === index ? 0 : -1}
                        aria-haspopup="dialog"
                        onClick={() => setSelectedBenefit(benefit)}
                      >
                        <img src={asset(benefit.benefitIcon)} alt="" draggable="false" />
                        <span>{benefit.benefitTitle}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </section>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
      {selectedBenefit && <BenefitDialog benefit={selectedBenefit} onClose={closeBenefit} />}
    </main>
  );
}
